import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import Utility from "../../Utility";
import GeneralDialog from "./GeneralDialog";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import FeaturedVideoOutlinedIcon from '@mui/icons-material/FeaturedVideoOutlined';
import {QRIcon} from "../../assets/svg-icons/QRIcon";
import ConditionalComponent from "../Shared/ConditionalComponent";
import {useStores} from "../../Hooks";
import {procedureTypeEnum, runProcedureInEnum, accessItemTypeEnum} from "../../serverClientShared/sharedEnums"
import ProcedureUtility from "../../Utilities/ProcedureUtility";

import {
    Checkbox,
    FormControl,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Tooltip
} from "@mui/material";
import ProceduresListDialogSingleProject from "./ProceduresListDialogSingleProject";
import InfoIcon from "@mui/icons-material/Info";
import withAttributesImg from "../../assets/img/with-attributes.png"
import withNoAttributesImg from "../../assets/img/with-no-attributes.png"
import {routes} from "../../App";
import {ProcedureRequester} from "../../MobxStores/RequestApi/ProcedureRequester";

const shareLinkEnums = {
    WEB_AND_APP: "web_and_app",
    APP_ONLY: "app_only",
    WEB_ONLY: "web_only",
}

const baseUrl = Utility.getBaseUrl();

const ShareItemModal = observer(() => {
    const {generalStore, modalStore, projectStore, procedureStore, dataStore} = useStores();
    const [selectedThreeDMode, setSelectedThreeDMode] = useState(0);
    const [proceduresModalOpen, setProceduresModalOpen] = useState(false);
    const [selectedProcedure, setSelectedProcedure] = useState(null);
    const [noAttributes, setNoAttributes] = useState(false);
    const [linkSetting, setLinkSetting] = useState(shareLinkEnums.WEB_AND_APP);
    const [shareLink, setShareLink] = useState('');
    const isFrontline3d = modalStore.modalData.procedureType === procedureTypeEnum.FUSION;
    const currentProcedure = projectStore.getProcedureFromCurrentProject(modalStore.modalData.sharingId);
    const hasOnlyWebGl = !currentProcedure?.ThreeDFilesAndroid && !currentProcedure?.ThreeDFilesHololens && !currentProcedure?.ThreeDFilesIOs && currentProcedure?.ThreeDFilesWebgl && !currentProcedure?.ThreeDFilesWin && !currentProcedure?.ThreeDFilesOsx;
    const procedureType = currentProcedure?.ProcedureType;
    useEffect(() => {
        if (modalStore.isShareProcedureModalOpen) {
            if (linkSetting !== shareLinkEnums.APP_ONLY) {
                const route = noAttributes ? `run/${generalStore.workspace}/1` : `deep/view/${generalStore.workspace}`;
                const params = linkSetting === shareLinkEnums.WEB_ONLY ? '?web-only=true' : '';
                const url = `/${route}/${getFullSharingId()}${params}`;
                shortenUrl(url).then(id => {
                    setShareLink(`${baseUrl}/s/${id}`);
                });

            } else {
                let procedureIdAndSettings = modalStore.modalData.sharingId;

                if (isFrontline3d) {
                    procedureIdAndSettings += '?';
                    if (selectedProcedure && selectedProcedure.ProcedureIdentifier) {
                        procedureIdAndSettings += '&connected_procedure=' + selectedProcedure.ProcedureIdentifier;
                    }
                    procedureIdAndSettings += '&mode_index=' + (selectedThreeDMode - 1);
                }
                let deepPath = Utility.buildUrl(routes.DEEP_LINK_VIEWER, {
                    db: generalStore.workspace,
                    procedureId: procedureIdAndSettings
                });
                shortenUrl(deepPath).then(id => {
                    const shortUrl = `s/${id}`;
                    setShareLink(`frn://${shortUrl}`);
                });
            }
        }// eslint-disable-next-line
    }, [modalStore.isShareProcedureModalOpen, generalStore.workspace, isFrontline3d, linkSetting, modalStore.modalData.sharingId, noAttributes, selectedProcedure, selectedThreeDMode]);

    const projectModes = projectStore.currentProject?.ThreeDProceduresMods;

    const getFullSharingId = () => {
        if (isFrontline3d) {
            const selectedProcedureStr = selectedProcedure && selectedProcedure.ProcedureIdentifier ? selectedProcedure.ProcedureIdentifier : '';
            return `${modalStore.modalData.sharingId}/${selectedThreeDMode - 1}/${selectedProcedureStr}`
        } else {
            return modalStore.modalData.sharingId
        }
    }

    async function shortenUrl(fullUrl) {
        return await ProcedureRequester.setShortUrl(fullUrl, {
            db: generalStore.workspace,
            itemType: accessItemTypeEnum.ITEM,
            itemId: modalStore.modalData.sharingId,
        });
    }


    const copyLink = async () => {
        await navigator.clipboard.writeText(shareLink);
        modalStore.showSuccessMessage(generalStore.translate('linkCopied'));
        closeModal();
    };

    const openQR = () => {
        let qrName;
        const procedure = projectStore.getProcedureFromCurrentProject(modalStore.modalData.sharingId);
        if (!!procedure) {
            qrName = procedure.ProcedureName;
        } else {
            qrName = modalStore.modalData.procedureName;
        }
        closeModal();
        generalStore.isShowPinCode = false;
        modalStore.isGenerateQRModalOpen = true;
        modalStore.modalData = {
            shareLink,
            qrName,
        };
        if(selectedProcedure){
            modalStore.modalData.attachedItemName = selectedProcedure.ProcedureName;
        }
    };

    const openEmbed = () => {
        const embedId = getFullSharingId();
        closeModal();
        modalStore.isEmbedProcedureModalOpen = true;
        modalStore.modalData = {
            embedId
        }
    }

    const handleSelectProcedure = procedure => {
        if (selectedProcedure?.ProcedureIdentifier === procedure.ProcedureIdentifier) {
            setSelectedProcedure(null)
        } else {
            setSelectedProcedure(procedure);
        }
    }

    function renderAttributesHint() {
        const imgSource = !noAttributes ? withAttributesImg : withNoAttributesImg;
        return (
            <img style={{width: 250}} src={imgSource} alt=''/>
        )
    }

    function renderAttributes() {
        const currentProcedure = procedureStore.currentProcedure;
        const projectProceduresMap = dataStore.projectsProceduresMap.get(projectStore.currentProject?.ProjectIdentifier);
        return (
            <div className="share-procedure-three-d-settings">
                <FormControl>
                    <RadioGroup
                        value={linkSetting}
                        onChange={e => setLinkSetting(e.target.value)}
                        className='procedure-link-setting-radio'
                    >

                        <ConditionalComponent if={ProcedureUtility.checkIfWebOnlyLink(currentProcedure, projectProceduresMap)}>
                            <div>
                                <FormControlLabel
                                    value={shareLinkEnums.WEB_ONLY}
                                    control={<Radio color='primary'/>}
                                    label={generalStore.translate('webOnly')}
                                />
                            </div>
                        </ConditionalComponent>
                        <div >

                            <div>
                                <FormControlLabel
                                    value={shareLinkEnums.WEB_AND_APP}
                                    control={<Radio color='primary'/>}
                                    label={generalStore.translate('webAndApp')}
                                />
                            </div>
                            <div>
                                <FormControlLabel
                                    className="select-procedure-viewer-attributes"
                                    disabled={linkSetting === shareLinkEnums.APP_ONLY}
                                    control={
                                        <Checkbox
                                            checked={!noAttributes}
                                            onChange={() => setNoAttributes(!noAttributes)}
                                            color='primary'
                                            name='attributes'
                                        />
                                    }
                                    label={
                                        <>
                                            <span>{generalStore.translate('attributes')}</span>
                                            &nbsp;

                                            <Tooltip title={renderAttributesHint()} arrow>
                                                <InfoIcon fontSize='small' onClick={e => e.preventDefault()}/>
                                            </Tooltip>
                                        </>
                                    }
                                />
                            </div>

                        </div>
                        <div>
                            <FormControlLabel
                                value={shareLinkEnums.APP_ONLY}
                                control={
                                    <Radio disabled={procedureType === procedureTypeEnum.FUSION && hasOnlyWebGl} color='primary'/>
                                }
                                label={generalStore.translate('appOnly')}
                            />

                        </div>
                    </RadioGroup>
                </FormControl>
            </div>
        );
    }

    function render3DOptions() {
        return <ConditionalComponent if={isFrontline3d}>
            <div className="share-procedure-three-d-settings">
                <TextField
                    disabled={!projectModes}
                    className="three-d-mode-select"
                    select
                    label={generalStore.translate("selectMode")}
                    value={selectedThreeDMode}
                    onChange={e => setSelectedThreeDMode(e.target.value)}
                >
                    <MenuItem value={0}>
                        {generalStore.translate("none")}
                    </MenuItem>
                    {!!projectModes && projectModes.map((mode, i) =>
                        <MenuItem key={`three-d-mode-select-${i}`} value={i + 1}>
                            {mode.name}
                        </MenuItem>
                    )}
                </TextField>
                <Tooltip title={generalStore.translate("linkOpensModelWithThisProcedure")}>
                    <TextField
                        className="three-d-model-sub-procedure-select"
                        label={generalStore.translate("selectStartingProcedure")}
                        value={selectedProcedure ? selectedProcedure.ProcedureName : generalStore.translate('noProcedureSelected')}
                        onClick={() => setProceduresModalOpen(true)}
                        InputProps={{
                            readOnly: true
                        }}
                    />
                </Tooltip>
            </div>
        </ConditionalComponent>;
    }

    const renderShareProcedureBody = () => {
        return (
            <div className="share-procedure-body">
                <div className="share-procedure-link-container">
                    <div className="procedure-stat-title">
                        {generalStore.translate('itemLink')}
                    </div>
                    <TextField
                        autoFocus
                        className='share-link-input'
                        readOnly
                        fullWidth
                        variant="outlined"
                        value={shareLink}
                        inputProps={{'data-testid': 'share-link-input'}}
                    />
                </div>
                <div className="share-procedure-btns">
                    <Button variant="outlined"
                            onClick={copyLink}><FileCopyOutlinedIcon/>{generalStore.translate('copyLink')}</Button>
                    <Button variant="outlined" onClick={openQR}><QRIcon/>{generalStore.translate('generateQRCode')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={openEmbed}
                        disabled={linkSetting === shareLinkEnums.APP_ONLY}
                        style={linkSetting === shareLinkEnums.APP_ONLY ? {
                            color: "rgba(0, 0, 0, 0.38)",
                            borderColor: "rgba(0, 0, 0, 0.38)"
                        } : null}
                    >
                        <FeaturedVideoOutlinedIcon/>{generalStore.translate('embed')}
                    </Button>
                </div>
                <div className="share-procedure-three-d-settings-title">
                    {generalStore.translate('additionalShareSettings')}
                </div>
                <div className="additional-share-settings-container">
                    {renderAttributes()}
                    {render3DOptions()}
                </div>
            </div>
        )
    }

    const closeModal = () => {
        modalStore.isShareProcedureModalOpen = false;
        modalStore.resetModalData();
        Utility.sleep(100).then(() => {
            setSelectedProcedure(null);
            setSelectedThreeDMode(0);
        });
    }

    return (
        <ConditionalComponent if={!!modalStore.modalData.sharingId}>
            <GeneralDialog
                specialOnClose={closeModal}
                openControl='isShareProcedureModalOpen'
                className='procedure-option-dialog'
                onEnter={null}
                title={generalStore.translate('share')}
                body={renderShareProcedureBody()}
                isConfirmDisabled={true}
            />
            <ProceduresListDialogSingleProject
                isSelectSingleProcedure
                open={proceduresModalOpen}
                closeModal={() => setProceduresModalOpen(false)}
                handleSelectProcedure={handleSelectProcedure}
                selectedProcedureId={selectedProcedure?.ProcedureIdentifier || ""}
                selectedSingleProcedureId={modalStore.modalData.sharingId}
            />
        </ConditionalComponent>
    );
})

export default ShareItemModal;
